import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IncidentDetail,
  IncidentStatus,
  IncidentSummary,
  IncidentsSummary,
} from '@blesk/contracts';
import { apiGet, apiPost, type QueryParams } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import { useProjectOptions, timezoneOf } from '../../shared/options';
import { INCIDENT_ACTION_LABEL, INCIDENT_STATUS_LABEL } from '../../shared/labels';
import { formatInProjectTz, periodKeyLabel } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { IntegrationBlock } from '../shell/IntegrationBlock';
import { plural } from '../shell/charts';
import {
  B24TaskLink,
  Dialog,
  EmptyRow,
  ErrorNote,
  ExportCsvLink,
  Pager,
  PeriodFilter,
  StatToggle,
  orDash,
} from '../shell/ui';

/** §9.4, §13 ТЗ, E3. Раздел «Инциденты». */
export function IncidentsPage() {
  return (
    <PermissionGate permission="results.read">
      <IncidentsInner />
    </PermissionGate>
  );
}

/** E3: Новый → В работе → Закрыт, ветка «Отклонён (ложный)». Возврата назад нет. */
const NEXT_STATUSES: Record<IncidentStatus, Array<'IN_PROGRESS' | 'CLOSED' | 'REJECTED'>> = {
  NEW: ['IN_PROGRESS', 'REJECTED'],
  IN_PROGRESS: ['CLOSED', 'REJECTED'],
  CLOSED: [],
  REJECTED: [],
};

function IncidentsInner() {
  const session = useSession();
  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [status, setStatus] = useUrlFilter('status');
  const [number, setNumber] = useUrlFilter('number');
  const [overdueOnly, setOverdueOnly] = useUrlFilter('overdueOnly');
  const [from, setFrom] = useUrlFilter('from');
  const [to, setTo] = useUrlFilter('to');
  // §11.2, спека 60-bitrix24: ссылка из задачи Bitrix24 ведёт на
  // `/incidents/{id}` и должна открывать карточку, а не список
  const { id: idFromUrl } = useParams({ strict: false }) as { id?: string };
  const [cardId, setCardId] = useState<string | null>(idFromUrl ?? null);

  const projects = useProjectOptions();
  // один объект отбора на таблицу, сводку и выгрузку (§14, E9): разойтись им негде
  const filters = { projectId, status, number, overdueOnly, from, to };
  const list = usePagedList<IncidentSummary>('incidents', '/api/incidents', filters);

  // В автономном режиме задач в портале нет, и колонка «Задача B24» была бы
  // столбцом прочерков во всю ширину таблицы (§4 КП)
  const showBitrixTask = session.mode !== 'STANDALONE';
  const columns = showBitrixTask ? 7 : 6;

  const zoneOf = (incident: IncidentSummary) =>
    timezoneOf(projects.data, incident.projectId, session.defaultTimezone);

  return (
    <>
      <h1>Инциденты</h1>
      <p className="muted">
        Один инцидент на запуск опроса; повторная негативная оценка второй карточки не даёт.
      </p>

      <div className="toolbar">
        <label>
          Объект
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(INCIDENT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <PeriodFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <span className="toolbar__spacer" />
        <ExportCsvLink path="/api/incidents/export" filters={filters} />
      </div>

      {/* Отбор по номеру приходит ссылкой из дашборда и карточки запуска: без
          видимого чипа таблица выглядела бы просто пустой */}
      {number && (
        <div className="chips">
          <span className="chip">
            Номер: {number}
            <button type="button" aria-label="Снять отбор по номеру" onClick={() => setNumber('')}>
              ✕
            </button>
          </span>
        </div>
      )}

      <ErrorNote error={list.error} />

      <IncidentsSummaryPanel
        filters={filters}
        status={status}
        onStatus={setStatus}
        overdueOnly={overdueOnly}
        onOverdueOnly={setOverdueOnly}
      />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {/* Сортировка идёт по дате создания, поэтому дата стоит второй
                  строкой при номере, а не отдельной колонкой: разнесённые по
                  колонкам, эта пара и «объект — администратор» забирали 380 px
                  и выталкивали остальное вбок (замер 18.08.2026). */}
              <th>Инцидент</th>
              <th>Объект и администратор</th>
              <th>Период</th>
              <th>Статус</th>
              <th>Менеджер</th>
              {showBitrixTask && <th>Задача B24</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={columns} loading={list.isPending} />}
            {list.items.map((incident) => (
              <tr key={incident.id}>
                <td>
                  {incident.number}
                  <div className="cell-note">
                    {formatInProjectTz(incident.createdAt, zoneOf(incident))}
                  </div>
                </td>
                <td>
                  {incident.projectName}
                  <div className="cell-note">{incident.personName}</div>
                </td>
                <td>{periodKeyLabel(incident.periodKey)}</td>
                <td>
                  <span className="cell-mark">
                    {INCIDENT_STATUS_LABEL[incident.status]}
                    {incident.overdue && <span className="badge badge--err">просрочен</span>}
                  </span>
                </td>
                <td>{orDash(incident.managerSnapshotName)}</td>
                {showBitrixTask && (
                  <td>
                    <B24TaskLink taskId={incident.bitrixTaskId} />
                  </td>
                )}
                <td className="row-actions">
                  <button type="button" onClick={() => setCardId(incident.id)}>
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {cardId && <IncidentCard id={cardId} onClose={() => setCardId(null)} />}
    </>
  );
}

/**
 * §13 ТЗ. Сводка над таблицей раздела.
 *
 * Считает сервер по тому же отбору, что и таблицу (`/api/incidents/summary`):
 * плитка «новых 12» над списком из восьми строк — это не украшение, а неверная
 * цифра. Статус и признак просрочки сервер из отбора сводки выбрасывает сам —
 * их ставят как раз эти плитки.
 */
function IncidentsSummaryPanel({
  filters,
  status,
  onStatus,
  overdueOnly,
  onOverdueOnly,
}: {
  filters: QueryParams;
  status: string;
  onStatus: (status: string) => void;
  overdueOnly: string;
  onOverdueOnly: (value: string) => void;
}) {
  const summary = useQuery({
    queryKey: ['incidents', 'summary', filters],
    queryFn: () => apiGet<IncidentsSummary>('/api/incidents/summary', filters),
    placeholderData: keepPreviousData,
  });

  const data = summary.data;
  if (summary.error) return <ErrorNote error={summary.error} />;
  if (!data) return <p className="muted">Считаем сводку…</p>;

  const { counts } = data;
  const toggleStatus = (next: string) => onStatus(status === next ? '' : next);

  return (
    <section className="section-block summary" aria-label="Сводка по отбору">
      <h2>
        Сводка по отбору{' '}
        <span className="muted">
          {counts.total === 0
            ? 'инцидентов нет'
            : plural(counts.total, ['инцидент', 'инцидента', 'инцидентов'])}
        </span>
      </h2>

      <div className="stat-grid">
        <StatToggle
          value={counts.new}
          label="Новые"
          active={status === 'NEW'}
          onClick={() => toggleStatus('NEW')}
        />
        <StatToggle
          value={counts.inProgress}
          label="В работе"
          active={status === 'IN_PROGRESS'}
          onClick={() => toggleStatus('IN_PROGRESS')}
        />
        <StatToggle
          value={counts.closed}
          label="Закрытые"
          active={status === 'CLOSED'}
          onClick={() => toggleStatus('CLOSED')}
        />
        <StatToggle
          value={counts.rejected}
          label="Отклонённые"
          active={status === 'REJECTED'}
          onClick={() => toggleStatus('REJECTED')}
        />
        {/* Это не статус, а нарушение срока реакции: рядом со статусными
            плитками слово «Просрочено» читалось как ещё один статус */}
        <StatToggle
          value={data.overdue}
          label="Реакция просрочена"
          alert
          active={overdueOnly === 'true'}
          onClick={() => onOverdueOnly(overdueOnly === 'true' ? '' : 'true')}
        />
      </div>
    </section>
  );
}

function IncidentCard({ id, onClose }: { id: string; onClose: () => void }) {
  const session = useSession();
  const canChangeStatus = useHasPermission('results.status');
  const queryClient = useQueryClient();

  const incident = useQuery({
    queryKey: ['incidents', 'detail', id],
    queryFn: () => apiGet<IncidentDetail>(`/api/incidents/${id}`),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['incidents'] });
  };

  const changeStatus = useMutation({
    mutationFn: (next: IncidentStatus) => apiPost(`/api/incidents/${id}/status`, { status: next }),
    onSuccess: refresh,
  });

  const data = incident.data;
  const rule = data ? readRuleSnapshot(data.ruleSnapshot) : null;

  return (
    <Dialog title={data ? `Инцидент ${data.number}` : 'Инцидент'} onClose={onClose}>
      <ErrorNote error={incident.error ?? changeStatus.error} />
      {!data && incident.isPending && <p className="muted">Загрузка…</p>}
      {data && rule && (
        <>
          <dl className="detail">
            <dt>Объект</dt>
            <dd>{data.projectName}</dd>
            <dt>Администратор</dt>
            <dd>{data.personName}</dd>
            <dt>Период</dt>
            <dd>{periodKeyLabel(data.periodKey)}</dd>
            <dt>Статус</dt>
            <dd>
              {INCIDENT_STATUS_LABEL[data.status]}
              {data.overdue && <span className="badge badge--err">просрочен</span>}
            </dd>
            <dt>Менеджер на момент события</dt>
            <dd>{orDash(data.managerSnapshotName)}</dd>
            <dt>Создан</dt>
            <dd>{formatInProjectTz(data.createdAt, data.projectTimezone)}</dd>
            <dt>Срок реакции</dt>
            <dd>{formatInProjectTz(data.reactionDueAt, data.projectTimezone)}</dd>
            <dt>Закрыт</dt>
            <dd>{formatInProjectTz(data.closedAt, data.projectTimezone)}</dd>
            {/* Задача есть только в интегрированном развёртывании (§4 КП) */}
            {session.mode !== 'STANDALONE' && (
              <>
                <dt>Задача Bitrix24</dt>
                <dd>
                  <B24TaskLink taskId={data.bitrixTaskId} />
                </dd>
              </>
            )}
            <dt>Опрос</dt>
            <dd>
              {/* Разбор начинается с ответов: инцидент без них — только номер */}
              <Link to="/results" search={{ entityId: data.runId }}>
                Открыть опрос
              </Link>
            </dd>
          </dl>

          <div className="section-block">
            <h3>Сработавшее правило</h3>
            <p className="muted">
              Снимок правила на момент публикации версии анкеты: изменение настройки задним числом
              инцидент не переписывает.
            </p>
            <dl className="kv">
              <dt>Порог</dt>
              <dd>{orDash(rule.threshold)}</dd>
              <dt>Нарушенные вопросы</dt>
              <dd>
                {rule.violations.length === 0
                  ? '—'
                  : rule.violations.map((violation) => (
                      <div key={violation.key}>
                        {violation.text} — {violation.value}
                      </div>
                    ))}
              </dd>
              <dt>Комментарий администратора</dt>
              <dd>{orDash(rule.comment)}</dd>
            </dl>

            <details className="tech">
              <summary>Технические подробности</summary>
              <dl className="kv">
                <dt>Внешний ключ</dt>
                <dd>
                  <code>{data.externalKey}</code>
                </dd>
              </dl>
              <pre className="snapshot">{JSON.stringify(data.ruleSnapshot, null, 2)}</pre>
            </details>
          </div>

          {canChangeStatus && NEXT_STATUSES[data.status].length > 0 && (
            <div className="row-actions">
              {NEXT_STATUSES[data.status].map((next) => (
                <button
                  key={next}
                  type="button"
                  className={next === 'REJECTED' ? 'btn-danger' : 'btn-primary'}
                  disabled={changeStatus.isPending}
                  onClick={() => changeStatus.mutate(next)}
                >
                  {INCIDENT_ACTION_LABEL[next]}
                </button>
              ))}
            </div>
          )}

          <IntegrationBlock
            integration={data.integration}
            timezone={data.projectTimezone}
            onRetried={refresh}
          />
        </>
      )}
    </Dialog>
  );
}

/** Читаемый вид снимка правила: порог, нарушенные вопросы, комментарий (§9.4). */
interface RuleSnapshotView {
  threshold: string | null;
  violations: Array<{ key: string; text: string; value: number }>;
  comment: string | null;
}

const OPERATOR_TEXT: Record<string, (value: number) => string> = {
  lte: (value) => `${value} и ниже`,
  lt: (value) => `ниже ${value}`,
  eq: (value) => `ровно ${value}`,
};

const SCOPE_TEXT: Record<string, string> = {
  any_required_scored: 'хотя бы по одному обязательному оценочному вопросу',
  all_required_scored: 'по всем обязательным оценочным вопросам',
  average: 'по средней оценке опроса',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Снимок хранится как есть, без схемы: правило могло измениться после того,
 * как инцидент был записан, и разбирать его строго — значит однажды показать
 * пустую карточку вместо старого инцидента. Поэтому всё читается по частям,
 * а непонятое остаётся в технических подробностях.
 */
function readRuleSnapshot(raw: unknown): RuleSnapshotView {
  const snapshot = isRecord(raw) ? raw : {};
  const rule = isRecord(snapshot.rule) ? snapshot.rule : {};

  const operator = typeof rule.operator === 'string' ? OPERATOR_TEXT[rule.operator] : undefined;
  const value = typeof rule.value === 'number' ? rule.value : null;
  const scope = typeof rule.scope === 'string' ? SCOPE_TEXT[rule.scope] : undefined;
  const threshold =
    operator && value !== null ? `Оценка ${operator(value)}${scope ? ` ${scope}` : ''}` : null;

  // Сервис пишет `violations`, ранние снимки — `triggered`; читаются оба
  const listed = Array.isArray(snapshot.violations)
    ? snapshot.violations
    : Array.isArray(snapshot.triggered)
      ? snapshot.triggered
      : [];

  const violations = listed.flatMap((item, index) => {
    if (!isRecord(item) || typeof item.value !== 'number') return [];
    const key = typeof item.questionKey === 'string' ? item.questionKey : String(index);
    const text =
      typeof item.questionText === 'string' && item.questionText ? item.questionText : key;
    return [{ key, text, value: item.value }];
  });

  const comment = typeof snapshot.comment === 'string' ? snapshot.comment.trim() : '';

  return { threshold, violations, comment: comment || null };
}
